import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { inquiryAPI, Inquiry, InquiryReply } from '../../api';
import { useAuth } from '../../contexts/AuthContext';

// 스타일드 컴포넌트
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const PageTitle = styled.h1`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.5rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const InquiryCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const InquiryHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const InquiryTitle = styled.h2`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 0.5rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.2rem;
  }
`;

const InquiryMeta = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const StatusBadge = styled.span<{ status: 'pending' | 'answered' | 'closed' }>`
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${({ status, theme }) => {
    switch (status) {
      case 'pending':
        return `
          background-color: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      case 'answered':
        return `
          background-color: ${theme.colors.info}20;
          color: ${theme.colors.info};
        `;
      case 'closed':
        return `
          background-color: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      default:
        return '';
    }
  }}
`;

const DateText = styled.span`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const InquiryContent = styled.div`
  padding: 1.5rem;
  white-space: pre-wrap;
  font-size: 0.95rem;
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 1rem;
    font-size: 0.9rem;
  }
`;

const RepliesContainer = styled.div`
  margin-top: 2rem;
`;

const RepliesHeader = styled.h3`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
  }
`;

const ReplyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReplyCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ReplyHeader = styled.div`
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
`;

const ReplyAuthor = styled.div`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
`;

const ReplyContent = styled.div`
  padding: 1.25rem;
  white-space: pre-wrap;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.25rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SecondaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.info};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: white;
  
  &:hover {
    opacity: 0.8;
  }
`;

const DangerButton = styled(Button)`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.danger}10;
  }
`;

const ReplyForm = styled.form`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-top: 1.5rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 0.95rem;
  resize: vertical;
  margin-bottom: 1rem;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const Loading = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1rem;
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  background-color: ${({ theme }) => theme.colors.danger}20;
  color: ${({ theme }) => theme.colors.danger};
  border-radius: 0.25rem;
  font-size: 0.9rem;
`;

const NoReplies = styled.div`
  padding: 1.5rem;
  text-align: center;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.95rem;
`;

// 상태 라벨 매핑
const statusLabels: Record<string, string> = {
  pending: '답변 대기',
  answered: '답변 완료',
  closed: '해결 완료'
};

const InquiryDetailPage: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [replies, setReplies] = useState<InquiryReply[]>([]);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // 로그인 확인
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/inquiry/${inquiryId}` } });
    }
  }, [isAuthenticated, navigate, inquiryId]);
  
  // 문의 및 답변 데이터 로드
  useEffect(() => {
    const fetchData = async () => {
      if (!inquiryId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // 문의 상세 정보 로드
        const inquiryResponse = await inquiryAPI.getInquiryById(Number(inquiryId));
        if (inquiryResponse.data.success) {
          setInquiry(inquiryResponse.data.inquiry);
          setReplies(inquiryResponse.data.inquiry.replies || []);
        } else {
          throw new Error('문의 정보를 불러오는데 실패했습니다');
        }
      } catch (err) {
        console.error('문의 데이터 로드 실패:', err);
        setError('문의 정보를 불러오는데 문제가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [inquiryId]);
  
  // 답변 제출 핸들러
  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inquiryId || !replyContent.trim()) return;
    
    try {
      setSubmitting(true);
      
      const response = await inquiryAPI.addReply(Number(inquiryId), replyContent.trim());
      
      if (response.data.success) {
        // 새 답변 추가
        setReplies([...replies, response.data.reply]);
        setReplyContent('');
        
        // 문의 상태 업데이트
        if (inquiry && inquiry.status === 'pending') {
          setInquiry({
            ...inquiry,
            status: 'answered'
          });
        }
      } else {
        throw new Error(response.data.message || '답변 작성에 실패했습니다');
      }
    } catch (err) {
      console.error('답변 작성 실패:', err);
      setError('답변 작성에 문제가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // 문의 상태 변경 핸들러
  const handleStatusChange = async (newStatus: 'closed') => {
    if (!inquiryId || !inquiry) return;
    
    try {
      const response = await inquiryAPI.closeInquiry(Number(inquiryId));
      
      if (response.data.success) {
        setInquiry({
          ...inquiry,
          status: newStatus
        });
      } else {
        throw new Error(response.data.message || '상태 변경에 실패했습니다');
      }
    } catch (err) {
      console.error('상태 변경 실패:', err);
      setError('상태 변경에 문제가 발생했습니다. 다시 시도해 주세요.');
    }
  };
  
  // 날짜 포맷 함수
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (loading && !inquiry) {
    return (
      <Container>
        <BackLink to="/inquiry">← 문의 목록으로 돌아가기</BackLink>
        <Loading>문의 정보를 불러오는 중입니다...</Loading>
      </Container>
    );
  }
  
  if (error && !inquiry) {
    return (
      <Container>
        <BackLink to="/inquiry">← 문의 목록으로 돌아가기</BackLink>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }
  
  if (!inquiry) {
    return (
      <Container>
        <BackLink to="/inquiry">← 문의 목록으로 돌아가기</BackLink>
        <ErrorMessage>문의를 찾을 수 없습니다</ErrorMessage>
      </Container>
    );
  }
  
  return (
    <Container>
      <PageHeader>
        <BackLink to="/inquiry">← 문의 목록으로 돌아가기</BackLink>
        <PageTitle>문의 상세 정보</PageTitle>
      </PageHeader>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <InquiryCard>
        <InquiryHeader>
          <InquiryTitle>{inquiry.title}</InquiryTitle>
          <InquiryMeta>
            <StatusBadge status={inquiry.status}>
              {statusLabels[inquiry.status]}
            </StatusBadge>
            <DateText>작성일: {formatDate(inquiry.created_at)}</DateText>
          </InquiryMeta>
        </InquiryHeader>
        <InquiryContent>{inquiry.content}</InquiryContent>
      </InquiryCard>
      
      <ActionButtons>
        <div>
          {inquiry.status === 'answered' && (
            <SecondaryButton onClick={() => handleStatusChange('closed')}>
              해결 완료로 표시
            </SecondaryButton>
          )}
        </div>
        <div>
          <DangerButton onClick={() => navigate('/inquiry')}>목록으로</DangerButton>
        </div>
      </ActionButtons>
      
      <RepliesContainer>
        <RepliesHeader>답변 {replies.length}개</RepliesHeader>
        
        {replies.length === 0 ? (
          <NoReplies>아직 답변이 없습니다.</NoReplies>
        ) : (
          <ReplyList>
            {replies.map(reply => (
              <ReplyCard key={reply.reply_id}>
                <ReplyHeader>
                  <ReplyAuthor>
                    {reply.is_admin ? '관리자' : user?.username}
                  </ReplyAuthor>
                  <DateText>{formatDate(reply.created_at)}</DateText>
                </ReplyHeader>
                <ReplyContent>{reply.content}</ReplyContent>
              </ReplyCard>
            ))}
          </ReplyList>
        )}
        
        {inquiry.status !== 'closed' && (
          <ReplyForm onSubmit={handleReplySubmit}>
            <TextArea
              placeholder="추가 작성하기"
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              required
            />
            <PrimaryButton type="submit" disabled={submitting}>
              {submitting ? '추가 전송 중...' : '추가 전송'}
            </PrimaryButton>
          </ReplyForm>
        )}
      </RepliesContainer>
    </Container>
  );
};

export default InquiryDetailPage; 